import type {
  GuildWarCandidatePoolMember,
  GuildWarRosterMemberPathSnapshot,
} from "@/core/guild-war/types"
import type {
  TeamBoardState,
  TeamKey,
  TeamRoster,
} from "@/lib/team-board-store"

import {
  getGuildWarRosterTeamKeys,
  hasGuildWarViceCommanderSlot,
  isGuildWarFlatTeamRoster,
} from "@/core/guild-war/roster-board"
import { GUILD_WAR_TEAM_DISPLAY } from "@/core/guild-war/types"
import { toGuildWarPathPairDisplayLabel } from "@/lib/guild-war-path-display"
import { memberDisplayLabel } from "@/lib/member-display-label"

const EXPORT_TEAM_META: Array<{
  key: TeamKey
  title: string
  headerClassName: string
  leaderClassName: string
  memberClassName: string
}> = [
  {
    key: "mobility",
    title: GUILD_WAR_TEAM_DISPLAY.mobility.title,
    headerClassName: "text-[#bfefff]",
    leaderClassName: "border-[#55c6f3] bg-[#cfeef8]",
    memberClassName: "border-[#55c6f3] bg-[#e9f8fd]",
  },
  {
    key: "attack",
    title: GUILD_WAR_TEAM_DISPLAY.attack.title,
    headerClassName: "text-[#ffc2cb]",
    leaderClassName: "border-[#f87989] bg-[#ffd8de]",
    memberClassName: "border-[#f87989] bg-[#ffe9ed]",
  },
  {
    key: "defense",
    title: GUILD_WAR_TEAM_DISPLAY.defense.title,
    headerClassName: "text-[#ffe9a8]",
    leaderClassName: "border-[#f5c94c] bg-[#fff0be]",
    memberClassName: "border-[#f5c94c] bg-[#fff8df]",
  },
]

export function RosterExportCard({
  displayDateLabel,
  board,
  candidateById,
  heading = "Team Roster",
}: {
  displayDateLabel: string
  board: TeamBoardState
  candidateById: Map<string, GuildWarCandidatePoolMember>
  heading?: string
}) {
  const teamMeta = EXPORT_TEAM_META.filter(({ key }) =>
    getGuildWarRosterTeamKeys(board).includes(key),
  )

  return (
    <div className="flex w-400 flex-col gap-10 bg-[#33454f] px-16 py-14 text-[#111827]">
      <div>
        <h1 className="font-serif text-6xl font-semibold tracking-tight text-[#e7a0c4]">
          {heading} - {displayDateLabel || "Guild War"}
        </h1>
      </div>

      <div
        className={`grid gap-10 ${teamMeta.length === 2 ? "grid-cols-2" : "grid-cols-3"}`}
      >
        {teamMeta.map((team) => {
          const roster = board[team.key]
          if (!roster) return null
          return (
            <ExportTeamColumn
              key={team.key}
              title={team.title}
              roster={roster}
              memberPaths={board.memberPaths}
              memberDisplayNames={board.memberDisplayNames}
              candidateById={candidateById}
              headerClassName={team.headerClassName}
              leaderClassName={team.leaderClassName}
              memberClassName={team.memberClassName}
            />
          )
        })}
      </div>

      <div className="grid gap-4">
        <div className="rounded-md border border-[#d17caf] bg-[#d995c0] px-6 py-4 text-[#fff8fb]">
          <p className="text-xl italic">Commanders:</p>
          <p className="mt-1 text-2xl font-semibold">
            {board.commanders
              .map((memberId) =>
                getExportMemberLabel(
                  memberId,
                  candidateById,
                  board.memberDisplayNames,
                ),
              )
              .join(" | ")}
          </p>
        </div>

        <div className="rounded-md border border-[#bbb4f6] bg-[#ece8ff] px-6 py-4 text-[#15182f]">
          <p className="text-xl italic">Halftime Performers:</p>
          <p className="mt-1 text-2xl font-semibold">
            {board.halftimePerformers
              .map((memberId, index) => {
                const label = getExportMemberLabel(
                  memberId,
                  candidateById,
                  board.memberDisplayNames,
                )
                return label === "Vacant"
                  ? `Vacant (${ordinal(index + 1)})`
                  : `${label} (${ordinal(index + 1)})`
              })
              .join(" | ")}
          </p>
        </div>
      </div>
    </div>
  )
}

function ExportTeamColumn({
  title,
  roster,
  memberPaths,
  memberDisplayNames,
  candidateById,
  headerClassName,
  leaderClassName,
  memberClassName,
}: {
  title: string
  roster: TeamRoster
  memberPaths: Record<string, GuildWarRosterMemberPathSnapshot>
  memberDisplayNames: Record<string, string>
  candidateById: Map<string, GuildWarCandidatePoolMember>
  headerClassName: string
  leaderClassName: string
  memberClassName: string
}) {
  return (
    <div className="flex min-h-0 flex-col">
      <h2
        className={`mb-4 text-center font-serif text-3xl font-semibold ${headerClassName}`}
      >
        {title}
      </h2>

      <div
        className={`mb-6 grid gap-4 ${hasGuildWarViceCommanderSlot(roster) ? "grid-cols-2" : ""}`}
      >
        <ExportLeaderCard
          label="Team Leader"
          memberId={roster.commander}
          candidateById={candidateById}
          memberDisplayNames={memberDisplayNames}
          className={leaderClassName}
        />
        {hasGuildWarViceCommanderSlot(roster) ? (
          <ExportLeaderCard
            label="Vice Leader"
            memberId={roster.viceCommander}
            candidateById={candidateById}
            memberDisplayNames={memberDisplayNames}
            className={leaderClassName}
          />
        ) : null}
      </div>

      {isGuildWarFlatTeamRoster(roster) ? (
        <div
          className={`grid flex-1 gap-4 ${roster.members.length === 13 ? "grid-cols-3" : ""}`}
        >
          {partitionMemberEntries(roster.members).map((lane) => (
            <div key={lane[0]!.index} className="grid content-start gap-0">
              {lane.map(({ memberId, index }, laneIndex) => (
                <ExportMemberCard
                  key={`member-${index}`}
                  memberId={memberId}
                  memberIndex={index}
                  memberPaths={memberPaths}
                  candidateById={candidateById}
                  memberDisplayNames={memberDisplayNames}
                  className={memberClassName}
                  isLast={laneIndex === lane.length - 1}
                />
              ))}
            </div>
          ))}
        </div>
      ) : (
        <div className="grid flex-1 grid-cols-2 gap-4">
          {roster.subteams.map((subteam) => (
            <div key={subteam.key} className="grid gap-0">
              {subteam.members.map((memberId, index) => (
                <ExportMemberCard
                  key={`${subteam.key}-${index}`}
                  memberId={memberId}
                  memberPaths={memberPaths}
                  candidateById={candidateById}
                  memberDisplayNames={memberDisplayNames}
                  className={memberClassName}
                  isLast={index === subteam.members.length - 1}
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function ExportLeaderCard({
  label,
  memberId,
  candidateById,
  memberDisplayNames,
  className,
}: {
  label: string
  memberId: string | null
  candidateById: Map<string, GuildWarCandidatePoolMember>
  memberDisplayNames: Record<string, string>
  className: string
}) {
  const name = getExportMemberLabel(memberId, candidateById, memberDisplayNames)

  return (
    <div
      className={`flex flex-col items-center justify-center gap-2 rounded-[20px] border-2 px-5 py-4 text-center shadow-sm ${className}`}
    >
      <p
        title={name}
        className="line-clamp-2 font-serif text-[26px] leading-tight font-semibold"
      >
        {name}
      </p>
      <p className="text-[18px] italic">{label}</p>
    </div>
  )
}

function ExportMemberCard({
  memberId,
  memberIndex,
  memberPaths,
  candidateById,
  memberDisplayNames,
  className,
  isLast,
}: {
  memberId: string | null
  memberIndex?: number
  memberPaths: Record<string, GuildWarRosterMemberPathSnapshot>
  candidateById: Map<string, GuildWarCandidatePoolMember>
  memberDisplayNames: Record<string, string>
  className: string
  isLast: boolean
}) {
  const name = getExportMemberLabel(memberId, candidateById, memberDisplayNames)
  const path = resolveMemberPathLabel(memberId, memberPaths, candidateById)

  return (
    <div
      className={`min-h-28 border-x-2 border-t-2 px-5 py-4 ${className} ${isLast ? "border-b-2" : ""}`}
    >
      {memberIndex === undefined ? null : (
        <p className="text-[14px] font-semibold uppercase">
          Member {memberIndex + 1}
        </p>
      )}
      <p
        title={name}
        className="line-clamp-2 font-serif text-[18px] leading-snug font-semibold italic"
      >
        {name}
      </p>
      <p title={path} className="mt-1 line-clamp-2 text-[16px] italic">
        ({path})
      </p>
    </div>
  )
}

function getExportMemberLabel(
  memberId: string | null,
  candidateById: Map<string, GuildWarCandidatePoolMember>,
  memberDisplayNames: Record<string, string>,
) {
  if (!memberId) {
    return "Vacant"
  }

  const override = memberDisplayNames[memberId]
  if (override) {
    return override
  }

  const candidate = candidateById.get(memberId)
  return memberDisplayLabel({
    displayName: candidate?.guildDisplayName,
  })
}

function partitionMemberEntries(members: Array<string | null>) {
  const laneLengths = members.length === 13 ? [5, 4, 4] : [members.length]
  let offset = 0
  return laneLengths.map((length) => {
    const lane = members
      .slice(offset, offset + length)
      .map((memberId, index) => ({ memberId, index: offset + index }))
    offset += length
    return lane
  })
}

function formatPathSnapshotLabel(snapshot: GuildWarRosterMemberPathSnapshot) {
  return toGuildWarPathPairDisplayLabel(
    snapshot.primaryPathCode,
    snapshot.secondaryPathCode,
    {
      variant: "short",
      noneLabel: "?",
    },
  )
}

function resolveMemberPathLabel(
  memberId: string | null,
  memberPaths: Record<string, GuildWarRosterMemberPathSnapshot>,
  candidateById: Map<string, GuildWarCandidatePoolMember>,
) {
  if (!memberId) {
    return "?"
  }

  const snapshot = memberPaths[memberId]
  if (snapshot?.primaryPathCode || snapshot?.secondaryPathCode) {
    return formatPathSnapshotLabel(snapshot)
  }

  const candidate = candidateById.get(memberId)
  return toGuildWarPathPairDisplayLabel(
    candidate?.primaryPathCode,
    candidate?.secondaryPathCode,
    { variant: "short", noneLabel: "?" },
  )
}

function ordinal(value: number) {
  return value === 1 ? "1st" : value === 2 ? "2nd" : "3rd"
}
